import React, { useState, useEffect } from 'react'
import { Icon, Tabs, Select } from 'antd'
import { Desk } from './desk/Index'
import { Clone } from './clone/Index'
import { Team } from './team/Index'
import { Sms } from './channels/sms/Index'
import { Migrate } from './migrate/Index'
import { Overview } from './overview/Index'
import { Campaign } from './campaign/Index'
import { Analytics } from './analytics/Index'
import { Builder } from './builder/Index'
import { Contacts } from './contacts/Index'
import { Configurations } from './configurations/Index'
import { OrganizationTeam } from '../organization/team/Index'
import { WhatsappInformation } from './channels/whatsapp/Index'
import { OverviewRequest } from 'models/request/Index'
import { PushNotification } from 'utils/PushNotification'
import { getApplication } from 'services/CeberusApiService'
import { TenantService } from 'connection/TenantService'
import { LoadingFactory } from 'components/LoadingFactory'
import { BotHealth } from "./bothealth/Index"

const Components = {
  Desk: 'desk',
  BotHealth: 'bothealth',
  Overview: 'overview',
  Analytics: 'analytics',
  Configurations: 'configurations',
  Team: 'team',
  Builder: 'builder',
  Contacts: 'contacts',
  Clone: 'clone',
  Migrate: 'migrate',
  Sms : 'sms',
  Whatsapp : 'whatsapp',
  OrganizationTeam: 'organizationteam',
  Campaign: 'campaign'
}

const BLIP_CHAT_APP_KEY = 'blipchat-app-key';
const AT = '@';

export const Application = (props: any) => {
  const [isLoading, setLoading] = useState(false);
  const [activeApplication, setActiveApplication] = useState(null);
  const [applications, setApplications] = useState([]);
  const [clusterSelected, setCluster] = useState(null);

  useEffect(() => {
    if (applications.length && !activeApplication) {
      setActiveApplication(applications[0].extras[BLIP_CHAT_APP_KEY])
    }
    const selectedTenant = TenantService.getSelectedTenant();
    
    const clusterName = selectedTenant != null ? selectedTenant.name || selectedTenant.defaultCluster : null;
    setCluster(clusterName);
  }, [applications, clusterSelected])

  const changeAccount = (account: any) : any => {
    account.identity = account.identity.Name + AT + account.identity.Domain;
    delete account.identity.Name;
    delete account.identity.Domain;
    return account;
  }

  const addApplicationByName = async (ownerIdentity: string) : Promise<void> => {

    if(applications.find(app => app.identity.split(AT).shift().includes(ownerIdentity))){
      return;
    }

    const tenantSelected = TenantService.getSelectedTenant();
    const request = new OverviewRequest();
    request.BotIdentifier = ownerIdentity;
    request.ClusterName = tenantSelected.name;
    const application = await getApplication(request);

    if (application) {
      const tenant = application.Tenant;
      const account = changeAccount(application.Account);
      const { BLIP_CHAT_APP_KEY: key } = account.extras;
      const currentApplicationInState = applications.find(({ extras }) => extras[BLIP_CHAT_APP_KEY] === key);
      if (!currentApplicationInState) {
        setApplications(previousState => [...previousState, { ...account, tenant }]);
      }
    }
  }

  const removeApplicationByName = (name: string) : void => {
    setApplications(previousState => [...previousState].filter(application => (
      application.fullName.toLowerCase() !== name.toLowerCase()
    )))
  }

  const removeApplicationByKey = (key: string) : void => {
    setApplications(previousState => [...previousState].filter(application => (
      application.extras[BLIP_CHAT_APP_KEY] !== key
    )))
  }

  const onSearch = async (identifiers: any) : Promise<void> => {
    if (!identifiers || !identifiers.length) {
      return;
    }

    const addItems = identifiers.filter((identifier: string) => (
      !applications.some(application => (
        application.fullName.toLowerCase() === identifier.toLowerCase()
      ))
    ));

    const addAll = Promise.all(addItems.map(addApplicationByName));

    try {
      setLoading(true);
      await addAll
    } catch (error) {
      PushNotification.httpError(error);
    } finally {
      setLoading(false);
    }
  }

  const getCurrentComponent = (currentApplication: any) => {
    const userAuth = {};
    const componentProps = { currentApplication, isLoading, userAuth };
    const component = props.match.params.application || props.match.params.organizationteam || '';

    switch (component.toLowerCase()) {
      case Components.Team:
        return <Team BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Desk:
        return <Desk BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Analytics:
        return <Analytics BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Configurations:
        return <Configurations BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Builder:
        return <Builder BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Contacts:
        return <Contacts BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Clone:
        return <Clone BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Migrate:
        return <Migrate BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Sms:
        return <Sms BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.BotHealth:
        return <BotHealth BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.Whatsapp:
        return <WhatsappInformation BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />
      case Components.OrganizationTeam:
        return <OrganizationTeam BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} MetaData={null} />  
      case Components.Campaign:
        return <Campaign BotIdentifier={componentProps.currentApplication.identity} ClusterName={clusterSelected} PaymentAccount={null} MetaData={null} />
      default:
        return <Overview BotIdentifier={componentProps.currentApplication.identity} PaymentAccount={null} ClusterName={clusterSelected} MetaData={null} />
    }
  }

  return (
    <div className="flex flex-wrap">
      <div className="tc w-100">
        <h1>
          <Icon type="robot" className="mr3" />
          Search Chatbot by id
        </h1>
        <Select
          tokenSeparators={[',', ';', ' ']}
          mode="tags"
          allowClear
          loading={isLoading}
          onChange={onSearch}
          onDeselect={removeApplicationByName}
          style={{ width: '100%' }}
        />
      </div>

      <div className="mt5 w-100">
        {
          isLoading ? <LoadingFactory />
            : <Tabs
              hideAdd
              onChange={setActiveApplication}
              activeKey={activeApplication}
              type="editable-card"
              onEdit={removeApplicationByKey}>
              {applications.length ? applications.map(app => (
                <Tabs.TabPane
                  tab={app.fullName}
                  key={app.extras[BLIP_CHAT_APP_KEY]}
                  closable>{getCurrentComponent({ ...app, identity: app.identity.toLowerCase() })}
                </Tabs.TabPane>
              )) : null}
            </Tabs>
        }
      </div>
     
    </div>
  )
}